import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

const TemplateManagement: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>("")
  const [previewTemplate, setPreviewTemplate] = useState<any>(null)

  const {
    data: templates,
    isLoading,
    refetch,
  } = trpc.contentManagement.listTemplates.useQuery({
    category: selectedCategory || undefined,
    limit: 50,
  })

  const createTemplateMutation = trpc.contentManagement.createTemplate.useMutation()
  // const updateTemplateMutation = trpc.contentManagement.updateTemplate.useMutation(); // TODO: Add to backend
  // const useTemplateMutation = trpc.contentManagement.useTemplate.useMutation(); // TODO: Add to backend

  const handleCreateTemplate = async (templateData: any) => {
    try {
      await createTemplateMutation.mutateAsync(templateData)
      setIsCreateModalOpen(false)
      void refetch()
    } catch (error) {
      console.error("Failed to create template:", error)
    }
  }

  const handleUpdateTemplate = (templateId: string, updates: any) => {
    try {
      // TODO: Implement updateTemplate mutation in backend
      // await updateTemplateMutation.mutateAsync({ templateId, ...updates });
      console.log("Update template:", templateId, updates)
      setEditingTemplate(null)
      void refetch()
    } catch (error) {
      console.error("Failed to update template:", error)
    }
  }

  const handleUseTemplate = (templateId: string, variables: Record<string, string>) => {
    try {
      // TODO: Implement useTemplate mutation in backend
      // await useTemplateMutation.mutateAsync({ templateId, variables });
      console.log("Use template:", templateId, variables)
      // Optionally navigate to content creation or show success
    } catch (error) {
      console.error("Failed to use template:", error)
    }
  }

  const categories = [
    "general",
    "product_launch",
    "thought_leadership",
    "engagement",
    "announcement",
    "personal_story",
    "tips_advice",
    "behind_scenes",
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Template Management</h2>
          <p className="">Create and manage reusable content templates</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Template
        </button>
      </div>

      {/* Category Filter */}
      <div className="bg-background p-4 rounded-lg shadow">
        <label className="block text-sm font-medium text-primary mb-2">Filter by Category:</label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              selectedCategory === "" ? "bg-blue-600 text-white" : "bg-foregroundx text-primary "
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1 rounded-full text-sm transition-colors capitalize ${
                selectedCategory === category ? "bg-blue-600 text-white" : "bg-foregroundx text-primary "
              }`}
            >
              {category.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates?.map((template: any) => (
          <div key={template.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-medium  line-clamp-2">{template.name}</h3>
                <p className="text-sm text-gray-500 mt-1 capitalize">{template.category.replace("_", " ")}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setPreviewTemplate(template)} className=" hover:text-blue-800">
                  👁️
                </button>
                <button onClick={() => setEditingTemplate(template)} className="text-gray-400 hover:">
                  ✏️
                </button>
              </div>
            </div>

            {template.description && <p className="text-primary text-sm mb-4 line-clamp-2">{template.description}</p>}

            {/* Template Preview */}
            <div className="bg-foregroundx p-3 rounded mb-4">
              <div className="text-xs  mb-1">Template Preview:</div>
              <p className="text-sm text-primary line-clamp-3">{template.structure}</p>
            </div>

            {/* Variables */}
            {template.variables && template.variables.length > 0 && (
              <div className="mb-4">
                <div className="text-xs  mb-1">Variables:</div>
                <div className="flex flex-wrap gap-1">
                  {template.variables.slice(0, 3).map((variable: string, idx: number) => (
                    <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      {variable}
                    </span>
                  ))}
                  {template.variables.length > 3 && (
                    <span className="px-2 py-1 bg-foregroundx text-gray-500 text-xs rounded">
                      +{template.variables.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Usage Stats */}
            <div className="flex justify-between text-xs text-gray-500 mb-4">
              <span>Used: {template.usageCount || 0} times</span>
              <span>Created: {new Date(template.createdAt).toLocaleDateString()}</span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => setPreviewTemplate(template)}
                className="w-full bg-blue-100 text-blue-700 py-2 px-3 rounded text-sm hover:bg-blue-200 transition-colors"
              >
                Use Template
              </button>
              <button
                onClick={() => setEditingTemplate(template)}
                className="w-full bg-foregroundx text-primary py-2 px-3 rounded text-sm  transition-colors"
              >
                Edit Template
              </button>
            </div>
          </div>
        ))}

        {(!templates || templates.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📝</div>
            <h3 className="text-lg font-medium  mb-2">No templates yet</h3>
            <p className=" mb-4">
              {selectedCategory
                ? `No templates found in the "${selectedCategory.replace("_", " ")}" category.`
                : "Create your first template or approve some scraped content as templates."}
            </p>
            {!selectedCategory && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Template
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {isCreateModalOpen && (
        <CreateTemplateModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateTemplate}
          isLoading={createTemplateMutation.isLoading}
        />
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSubmit={(updates) => handleUpdateTemplate(editingTemplate.id, updates)}
          isLoading={false}
        />
      )}

      {/* Preview/Use Template Modal */}
      {previewTemplate && (
        <UseTemplateModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSubmit={(variables) => handleUseTemplate(previewTemplate.id, variables)}
          isLoading={false}
        />
      )}
    </div>
  )
}

const CreateTemplateModal: React.FC<{
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    structure: "",
    category: "general",
    variables: [] as string[],
  })
  const [newVariable, setNewVariable] = useState("")

  // Extract variables from structure automatically
  const extractVariables = () => {
    const matches = formData.structure.match(/\{\{(\w+)\}\}/g)
    if (matches) {
      const variables = matches.map((match) => match.replace(/[{}]/g, ""))
      const uniqueVariables = [...new Set(variables)]
      setFormData((prev) => ({ ...prev, variables: uniqueVariables }))
    }
  }

  const addVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData((prev) => ({
        ...prev,
        variables: [...prev.variables, newVariable.trim()],
      }))
      setNewVariable("")
    }
  }

  const removeVariable = (variableToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((variable) => variable !== variableToRemove),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Create New Template</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Template Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Product Launch Announcement"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="general">General</option>
              <option value="product_launch">Product Launch</option>
              <option value="thought_leadership">Thought Leadership</option>
              <option value="engagement">Engagement</option>
              <option value="announcement">Announcement</option>
              <option value="personal_story">Personal Story</option>
              <option value="tips_advice">Tips & Advice</option>
              <option value="behind_scenes">Behind the Scenes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Brief description of when and how to use this template"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-primary">Template Structure</label>
              <button type="button" onClick={extractVariables} className="text-sm  hover:text-blue-800">
                Extract Variables
              </button>
            </div>
            <textarea
              required
              value={formData.structure}
              onChange={(e) => setFormData({ ...formData, structure: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={6}
              placeholder="Write your template with variables like {{company}}, {{product}}, {{benefit}}, etc."
            />
            <p className="text-xs text-gray-500 mt-1">Use double curly braces for variables: {`{{variable_name}}`}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Variables</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addVariable())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Variable name..."
              />
              <button
                type="button"
                onClick={addVariable}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.variables.map((variable, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-foregroundx text-primary text-sm rounded-full flex items-center"
                >
                  {variable}
                  <button
                    type="button"
                    onClick={() => removeVariable(variable)}
                    className="ml-2 text-destructive hover:text-destructive"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Creating..." : "Create Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditTemplateModal: React.FC<{
  template: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ template, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description || "",
    structure: template.structure,
    category: template.category,
    variables: template.variables || [],
  })
  const [newVariable, setNewVariable] = useState("")

  const extractVariables = () => {
    const matches = formData.structure.match(/\{\{(\w+)\}\}/g)
    if (matches) {
      const variables = matches.map((match) => match.replace(/[{}]/g, ""))
      const uniqueVariables = [...new Set(variables)]
      setFormData((prev) => ({ ...prev, variables: uniqueVariables }))
    }
  }

  const addVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData((prev) => ({
        ...prev,
        variables: [...prev.variables, newVariable.trim()],
      }))
      setNewVariable("")
    }
  }

  const removeVariable = (variableToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((variable) => variable !== variableToRemove),
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Edit Template</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Template Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="general">General</option>
              <option value="product_launch">Product Launch</option>
              <option value="thought_leadership">Thought Leadership</option>
              <option value="engagement">Engagement</option>
              <option value="announcement">Announcement</option>
              <option value="personal_story">Personal Story</option>
              <option value="tips_advice">Tips & Advice</option>
              <option value="behind_scenes">Behind the Scenes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-primary">Template Structure</label>
              <button type="button" onClick={extractVariables} className="text-sm  hover:text-blue-800">
                Extract Variables
              </button>
            </div>
            <textarea
              required
              value={formData.structure}
              onChange={(e) => setFormData({ ...formData, structure: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Variables</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addVariable())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                placeholder="Variable name..."
              />
              <button
                type="button"
                onClick={addVariable}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.variables.map((variable, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-foregroundx text-primary text-sm rounded-full flex items-center"
                >
                  {variable}
                  <button
                    type="button"
                    onClick={() => removeVariable(variable)}
                    className="ml-2 text-destructive hover:text-destructive"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Updating..." : "Update Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const UseTemplateModal: React.FC<{
  template: any
  onClose: () => void
  onSubmit: (variables: Record<string, string>) => void
  isLoading: boolean
}> = ({ template, onClose, onSubmit, isLoading }) => {
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState("")

  // Initialize variables
  React.useEffect(() => {
    if (template.variables) {
      const initialVariables: Record<string, string> = {}
      template.variables.forEach((variable: string) => {
        initialVariables[variable] = ""
      })
      setVariables(initialVariables)
    }
  }, [template.variables])

  // Update preview when variables change
  React.useEffect(() => {
    let previewText = template.structure
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g")
      previewText = previewText.replace(regex, value || `{{${key}}}`)
    })
    setPreview(previewText)
  }, [variables, template.structure])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(variables)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Use Template: {template.name}</h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Variables Form */}
          <div>
            <h4 className="font-medium  mb-4">Fill in Variables</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              {template.variables?.map((variable: string) => (
                <div key={variable}>
                  <label className="block text-sm font-medium text-primary mb-1 capitalize">
                    {variable.replace("_", " ")}
                  </label>
                  <input
                    type="text"
                    value={variables[variable] || ""}
                    onChange={(e) =>
                      setVariables((prev) => ({
                        ...prev,
                        [variable]: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    placeholder={`Enter ${variable.replace("_", " ")}`}
                  />
                </div>
              ))}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "Creating Content..." : "Create Content"}
                </button>
              </div>
            </form>
          </div>

          {/* Preview */}
          <div>
            <h4 className="font-medium  mb-4">Preview</h4>
            <div className="bg-foregroundx p-4 rounded-lg border h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-primary font-sans">{preview}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplateManagement
